import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  Alert,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  FormErrorMessage,
  useColorModeValue,
  Spinner,
  Center,
  Badge,
  Box
} from '@chakra-ui/react';
import { ChevronRightIcon, EditIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const EditSchedule = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    scheduled_at: '',
    action: '',
    notes: ''
  });
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (id && tenantId) {
      fetchPublication();
    }
  }, [id, tenantId]);

  const fetchPublication = async () => {
    try {
      setLoading(true);
      console.log('Fetching publication with ID:', id, 'for tenant:', tenantId);
      const response = await apiClient(`/schedule/${tenantId}/${id}`);
      console.log('Raw publication response:', response);
      
      let publicationData = null;
      
      // Handle different response structures
      if (response && response.success && response.data) {
        // Format: { success: true, data: {...} }
        publicationData = response.data;
      } else if (response && response.status === 'success' && response.data) {
        // Format: { status: "success", data: {...} }
        publicationData = response.data;
      } else if (response && response.id) {
        // Direct publication response
        publicationData = response;
      } else if (response && typeof response === 'object' && !Array.isArray(response)) {
        // Fallback: assume response is the publication itself
        publicationData = response;
      } else {
        console.warn('Unexpected response structure:', response);
        throw new Error('Invalid response format');
      }
      
      console.log('Processed publication data:', publicationData);
      
      if (!publicationData || !publicationData.id) {
        throw new Error('Publication not found');
      }
      
      setPublication(publicationData);
      
      // Format the datetime for the input field
      let scheduledAt = publicationData.scheduled_at;
      if (scheduledAt) {
        // Handle different datetime formats
        const date = new Date(scheduledAt);
        if (!isNaN(date.getTime())) {
          // Convert to datetime-local format (YYYY-MM-DDTHH:MM)
          scheduledAt = date.toISOString().slice(0, 16);
        } else {
          console.warn('Invalid scheduled_at date:', publicationData.scheduled_at);
          scheduledAt = '';
        }
      }
      
      setFormData({
        scheduled_at: scheduledAt,
        action: publicationData.action || '',
        notes: publicationData.notes || ''
      });
      
      console.log('Form data set to:', {
        scheduled_at: scheduledAt,
        action: publicationData.action || '',
        notes: publicationData.notes || ''
      });
      
    } catch (error) {
      console.error('Error fetching publication:', error);
      toast({
        title: 'Error loading publication',
        description: error.message || 'Failed to load scheduled publication',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/scheduling');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.scheduled_at) {
      newErrors.scheduled_at = 'Scheduled date/time is required';
    } else {
      const scheduledDate = new Date(formData.scheduled_at);
      const now = new Date();
      
      // Check if date is valid
      if (isNaN(scheduledDate.getTime())) {
        newErrors.scheduled_at = 'Please enter a valid date and time';
      } else if (scheduledDate <= now) {
        newErrors.scheduled_at = 'Scheduled time must be in the future';
      }
    }

    if (!formData.action) {
      newErrors.action = 'Action is required';
    } else if (!['publish', 'unpublish', 'archive'].includes(formData.action)) {
      newErrors.action = 'Please select a valid action';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      console.log('Updating publication with data:', formData);
      
      const response = await apiClient(`/schedule/${tenantId}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('Update response:', response);

      // Handle different success response formats
      if (response && (response.success || response.status === 'success' || response.id || response.message)) {
        toast({
          title: 'Publication updated',
          description: 'Your scheduled publication has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/scheduling');
      } else {
        throw new Error(response?.message || response?.error || 'Failed to update publication');
      }
    } catch (error) {
      console.error('Error updating publication:', error);
      toast({
        title: 'Error updating publication',
        description: error.message || 'Failed to update scheduled publication',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Minimum 5 minutes from now
    return now.toISOString().slice(0, 16);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'blue';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'gray';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <Center h="50vh">
          <VStack spacing={4}>
            <Spinner size="lg" />
            <Text>Loading scheduled publication...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  if (!publication) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          Publication not found or access denied.
        </Alert>
      </Container>
    );
  }

  // Enhanced status check with more detailed information
  if (publication.status && publication.status !== 'pending') {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="warning">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text>
              This publication cannot be edited because it has status: <Badge colorScheme={getStatusColor(publication.status)}>{publication.status}</Badge>
            </Text>
            <Text fontSize="sm" color="gray.600">
              Only publications with "pending" status can be modified.
            </Text>
          </VStack>
        </Alert>
        <Box mt={4}>
          <Button as={RouterLink} to="/scheduling" colorScheme="blue">
            Back to Scheduling
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/scheduling">Scheduling</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit Schedule</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <Heading size="lg" mb={4}>Edit Scheduled Publication</Heading>
            </Box>
            <Box>
              <Button
                as={RouterLink}
                to="/scheduling"
                variant="outline"
                colorScheme="gray"
                leftIcon={<EditIcon />}
              >
                Back to Scheduling
              </Button>
            </Box>
          </HStack>

          <Card borderWidth={1} borderColor={borderColor} borderRadius="md" bg={cardBg}>
            <CardHeader>
              <Heading size="md">Publication Details</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={!!errors.scheduled_at}>
                  <FormLabel htmlFor="scheduled_at">Scheduled Date/Time</FormLabel>
                  <Input
                    id="scheduled_at"
                    name="scheduled_at"
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    min={getMinDateTime()}
                  />
                  <FormErrorMessage>{errors.scheduled_at}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.action}>
                  <FormLabel htmlFor="action">Action</FormLabel>
                  <Select
                    id="action"
                    name="action"
                    placeholder="Select action"
                    value={formData.action}
                    onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                  >
                    <option value="publish">Publish</option>
                    <option value="unpublish">Unpublish</option>
                  </Select>
                  <FormErrorMessage>{errors.action}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="notes">Notes</FormLabel>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes for this publication"
                  />
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          <HStack justify="flex-end">
            <Button
              colorScheme="blue"
              isLoading={saving}
              type="submit"
            >
              Save Changes
            </Button>
          </HStack>
        </VStack>
      </form>
    </Container>
  );
};

export default EditSchedule;